import { ReactNode, useRef } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";

interface HorizontalScrollSectionProps {
  children: ReactNode;
  title?: string;
  subtitle?: string;
  className?: string;
  id?: string;
}

const HorizontalScrollSection = ({
  children,
  title,
  subtitle,
  className = "",
  id,
}: HorizontalScrollSectionProps) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const scroll = (direction: "left" | "right") => {
    if (scrollContainerRef.current) {
      const isMobile = window.innerWidth < 768;
      const scrollAmount = isMobile ? window.innerWidth * 0.85 + 16 : 420; // Card width + gap
      const newScrollLeft =
        scrollContainerRef.current.scrollLeft +
        (direction === "right" ? scrollAmount : -scrollAmount);
      scrollContainerRef.current.scrollTo({
        left: newScrollLeft,
        behavior: "smooth",
      });
    }
  };

  return (
    <section
      id={id}
      className={`relative py-16 sm:py-20 md:py-24 scroll-mt-24 ${className}`}
    >
      {/* Gradient Background */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-accent/5 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-primary/5 rounded-full blur-3xl" />
      </div>

      <div className="container mx-auto px-6 relative z-10">
        {title && (
          <div className="text-center mb-12 sm:mb-16 md:mb-20">
            <h2 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-foreground tracking-[-0.02em] mb-4 relative inline-block">
              {title}
              <span className="absolute -bottom-2 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-accent to-transparent" />
            </h2>
            {subtitle && (
              <p className="text-lg sm:text-xl md:text-2xl text-muted-foreground mt-6">
                {subtitle}
              </p>
            )}
          </div>
        )}

        <div className="relative group">
          {/* Navigation Arrows - Always Visible on Mobile, Hover on Desktop */}
          <Button
            variant="outline"
            size="icon"
            className="absolute left-2 md:left-0 top-1/2 -translate-y-1/2 md:-translate-x-6 z-10 md:opacity-0 md:group-hover:opacity-100 transition-opacity duration-300 bg-background/80 backdrop-blur-sm border-accent/20 hover:border-accent hover:bg-accent/10 shadow-lg"
            onClick={() => scroll("left")}
            aria-label="Scroll left"
          >
            <ChevronLeft className="w-5 h-5 md:w-6 md:h-6" />
          </Button>

          <Button
            variant="outline"
            size="icon"
            className="absolute right-2 md:right-0 top-1/2 -translate-y-1/2 md:translate-x-6 z-10 md:opacity-0 md:group-hover:opacity-100 transition-opacity duration-300 bg-background/80 backdrop-blur-sm border-accent/20 hover:border-accent hover:bg-accent/10 shadow-lg"
            onClick={() => scroll("right")}
            aria-label="Scroll right"
          >
            <ChevronRight className="w-5 h-5 md:w-6 md:h-6" />
          </Button>

          {/* Scrollable Container */}
          <div
            ref={scrollContainerRef}
            className="overflow-x-auto scrollbar-hide scroll-smooth"
          >
            <div className="flex gap-4 md:gap-8 snap-x snap-mandatory pb-4 min-w-max px-2 md:px-0">
              {Array.isArray(children) ? (
                children.map((child, index) => (
                  <div
                    key={index}
                    className="snap-center flex-shrink-0 w-[85vw] sm:w-[400px] md:w-[400px]"
                  >
                    {child}
                  </div>
                ))
              ) : (
                <div className="snap-center flex-shrink-0 w-[85vw] sm:w-[400px] md:w-[400px]">
                  {children}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HorizontalScrollSection;
